// Chemin: C:\gestion-copro\src\pages\admin\AffaireDetailPage.tsx
// Session 58 : Correction bug coproprietaireId → coproprieteIds[0]
// Session 44 : Double confirmation suppression + icône poubelle
// Session 46 : Lien document vers page détail (accès direct)
// Session 75 : Sécurité suppression (bouton visible uniquement pour Gestionnaire/Admin SaaS)

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import {
  Affaire,
  StatutAffaire,
  TYPE_AFFAIRE_LABELS,
  STATUT_AFFAIRE_LABELS,
  STATUT_AFFAIRE_COLORS,
  PRIORITE_AFFAIRE_LABELS,
  PRIORITE_AFFAIRE_COLORS,
  GARANTIE_LABELS,
  EvenementAffaire
} from '../../types/affaire.types';
import { Signalement } from '../../types/signalement.types';
import { Document } from '../../types/document.types';
import { Interlocuteur } from '../../types/interlocuteur.types';
import {
  getAffaireById,
  updateStatutAffaire,
  addEvenementToAffaire,
  rattacherSignalement,
  detacherSignalement,
  rattacherDocument,
  detacherDocument,
  deleteAffaire
} from '../../services/affaire.service';
import { getSignalements } from '../../services/signalement.service';
import { getDocuments } from '../../services/document.service';
import { getInterlocuteur } from '../../services/interlocuteur.service';

export default function AffaireDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userProfile, firebaseUser } = useAuth();

  const [affaire, setAffaire] = useState<Affaire | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Données liées
  const [signalementsLies, setSignalementsLies] = useState<Signalement[]>([]);
  const [documentsLies, setDocumentsLies] = useState<Document[]>([]);
  const [interlocuteursLies, setInterlocuteursLies] = useState<Interlocuteur[]>([]);

  // Pour rattachement
  const [showRattachementSignalement, setShowRattachementSignalement] = useState(false);
  const [showRattachementDocument, setShowRattachementDocument] = useState(false);
  const [signalementsDisponibles, setSignalementsDisponibles] = useState<Signalement[]>([]);
  const [documentsDisponibles, setDocumentsDisponibles] = useState<Document[]>([]);

  // Pour ajout note
  const [showAjoutNote, setShowAjoutNote] = useState(false);
  const [noteTexte, setNoteTexte] = useState('');

  // Pour changement statut
  const [showChangementStatut, setShowChangementStatut] = useState(false);
  const [nouveauStatut, setNouveauStatut] = useState<StatutAffaire>('en_cours');
  const [commentaireStatut, setCommentaireStatut] = useState('');

  // Pour suppression avec double confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const coproprieteId = userProfile?.coproprieteIds?.[0];

  // Session 75 : Helper pour la permission de suppression
  const canDeleteAffaire = userProfile?.isGestionnaireSaas === true || 
                           userProfile?.role === 'gestionnaire' || 
                           userProfile?.role === 'admin_saas';

  useEffect(() => {
    if (!id || !coproprieteId) return;

    const loadData = async () => {
      try {
        setLoading(true);
        
        // Charger l'affaire
        const affaireData = await getAffaireById(id);
        if (!affaireData) {
          setError('Affaire non trouvée');
          return;
        }
        setAffaire(affaireData);

        // Charger les signalements liés
        // Note: getSignalements() ne filtre pas par coproprieteId (à améliorer)
        const allSignalements = await getSignalements();
        const sigLies = allSignalements.filter(s => 
          affaireData.signalementIds.includes(s.id)
        );
        setSignalementsLies(sigLies);
        
        // Signalements disponibles (non rattachés à cette affaire)
        const sigDisponibles = allSignalements.filter(s => 
          !affaireData.signalementIds.includes(s.id)
        );
        setSignalementsDisponibles(sigDisponibles);

        // Charger les documents liés
        const allDocuments = await getDocuments(coproprieteId);
        const docsLies = allDocuments.filter(d => 
          affaireData.documentIds.includes(d.id)
        );
        setDocumentsLies(docsLies);
        
        // Documents disponibles (non rattachés à cette affaire)
        const docsDisponibles = allDocuments.filter(d => 
          !affaireData.documentIds.includes(d.id)
        );
        setDocumentsDisponibles(docsDisponibles);

        // Charger les interlocuteurs liés
        const interlocuteurs = await Promise.all(
          affaireData.interlocuteurIds.map(iId => getInterlocuteur(iId))
        );
        setInterlocuteursLies(interlocuteurs.filter(Boolean) as Interlocuteur[]);

      } catch (err) {
        console.error('Erreur chargement:', err);
        setError('Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, coproprieteId]);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Non renseigné';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('fr-FR');
  };

  const handleChangementStatut = async () => {
    if (!affaire || !firebaseUser) return;
    
    try {
      const userNom = `${userProfile?.prenom || ''} ${userProfile?.nom || ''}`.trim() || 'Utilisateur';
      await updateStatutAffaire(affaire.id, nouveauStatut, firebaseUser.uid, userNom, commentaireStatut);
      
      // Recharger l'affaire
      const updated = await getAffaireById(affaire.id);
      if (updated) setAffaire(updated);
      
      setShowChangementStatut(false);
      setCommentaireStatut('');
    } catch (err) {
      console.error('Erreur changement statut:', err);
      setError('Erreur lors du changement de statut');
    }
  };

  const handleAjoutNote = async () => {
    if (!affaire || !firebaseUser || !noteTexte.trim()) return;
    
    try {
      const userNom = `${userProfile?.prenom || ''} ${userProfile?.nom || ''}`.trim() || 'Utilisateur';
      await addEvenementToAffaire(affaire.id, {
        type: 'note',
        titre: 'Note ajoutée',
        description: noteTexte.trim(),
        auteurId: firebaseUser.uid,
        auteurNom: userNom
      });
      
      const updated = await getAffaireById(affaire.id);
      if (updated) setAffaire(updated);
      
      setShowAjoutNote(false);
      setNoteTexte('');
    } catch (err) {
      console.error('Erreur ajout note:', err);
      setError('Erreur lors de l\'ajout de la note');
    }
  };

  const handleRattacherSignalement = async (signalementId: string) => {
    if (!affaire || !firebaseUser) return;
    
    try {
      const userNom = `${userProfile?.prenom || ''} ${userProfile?.nom || ''}`.trim() || 'Utilisateur';
      await rattacherSignalement(affaire.id, signalementId, firebaseUser.uid, userNom);
      
      // Recharger
      window.location.reload();
    } catch (err) {
      console.error('Erreur rattachement:', err);
      setError('Erreur lors du rattachement');
    }
  };

  const handleDetacherSignalement = async (signalementId: string) => {
    if (!affaire) return;
    
    if (!window.confirm('Détacher ce signalement de l\'affaire ?')) return;
    
    try {
      await detacherSignalement(affaire.id, signalementId);
      window.location.reload();
    } catch (err) {
      console.error('Erreur détachement:', err);
      setError('Erreur lors du détachement');
    }
  };

  const handleRattacherDocument = async (documentId: string, documentTitre: string) => {
    if (!affaire || !firebaseUser) return;
    
    try {
      const userNom = `${userProfile?.prenom || ''} ${userProfile?.nom || ''}`.trim() || 'Utilisateur';
      await rattacherDocument(affaire.id, documentId, documentTitre, firebaseUser.uid, userNom);
      window.location.reload();
    } catch (err) {
      console.error('Erreur rattachement:', err);
      setError('Erreur lors du rattachement');
    }
  };

  const handleDetacherDocument = async (documentId: string) => {
    if (!affaire) return;
    
    if (!window.confirm('Détacher ce document de l\'affaire ?')) return;
    
    try {
      await detacherDocument(affaire.id, documentId);
      window.location.reload();
    } catch (err) {
      console.error('Erreur détachement:', err);
      setError('Erreur lors du détachement');
    }
  };

  const handleSupprimer = async () => {
    if (!affaire) return;
    
    setDeleting(true);
    try {
      await deleteAffaire(affaire.id);
      navigate('/admin/affaires');
    } catch (err) {
      console.error('Erreur suppression:', err);
      setError('Erreur lors de la suppression');
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const getEvenementIcon = (type: EvenementAffaire['type']) => {
    switch (type) {
      case 'creation': return 'O';
      case 'signalement': return 'S';
      case 'document': return 'D';
      case 'courrier': return 'C';
      case 'reunion': return 'R';
      case 'decision': return '!';
      case 'note': return 'N';
      case 'cloture': return 'X';
      default: return '-';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !affaire) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || 'Affaire non trouvée'}</p>
          <Link to="/admin/affaires" className="text-blue-600 hover:underline mt-2 inline-block">
            Retour aux affaires
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex justify-between items-start">
          <div>
            <button
              onClick={() => navigate('/admin/affaires')}
              className="text-gray-600 hover:text-gray-900 mb-2 inline-flex items-center text-sm"
            >
              &larr; Retour aux affaires
            </button>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{affaire.titre}</h1>
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${STATUT_AFFAIRE_COLORS[affaire.statut]}`}>
                {STATUT_AFFAIRE_LABELS[affaire.statut]}
              </span>
            </div>
            <p className="text-gray-500 mt-1">
              {affaire.numero} | {TYPE_AFFAIRE_LABELS[affaire.type]}
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              to={`/admin/affaires/${affaire.id}/modifier`}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Modifier
            </Link>
            <button
              onClick={() => setShowChangementStatut(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Changer statut
            </button>
          </div>
        </div>

        {/* Grille principale */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonne gauche - Infos et Timeline */}
          <div className="lg:col-span-2 space-y-6">
            {/* Informations */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Informations</h2>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <span className="text-sm text-gray-500">Priorité</span>
                  <div className={`inline-flex ml-2 px-2 py-1 text-xs font-semibold rounded-full ${PRIORITE_AFFAIRE_COLORS[affaire.priorite]}`}>
                    {PRIORITE_AFFAIRE_LABELS[affaire.priorite]}
                  </div>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Ouverte le</span>
                  <div className="text-gray-900">{formatDateShort(affaire.dateOuverture)}</div>
                </div>
                {affaire.garantieApplicable && (
                  <div className="col-span-2">
                    <span className="text-sm text-gray-500">Garantie applicable</span>
                    <div className="text-gray-900">{GARANTIE_LABELS[affaire.garantieApplicable]}</div>
                  </div>
                )}
                {affaire.montantEstime && (
                  <div>
                    <span className="text-sm text-gray-500">Montant estimé</span>
                    <div className="text-gray-900">{affaire.montantEstime.toLocaleString('fr-FR')} EUR</div>
                  </div>
                )}
              </div>
              
              <div>
                <span className="text-sm text-gray-500">Description</span>
                <p className="text-gray-900 mt-1 whitespace-pre-wrap">{affaire.description}</p>
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Chronologie</h2>
                <button
                  onClick={() => setShowAjoutNote(true)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  + Ajouter une note
                </button>
              </div>
              
              <div className="space-y-4">
                {affaire.evenements
                  .sort((a, b) => b.date.toMillis() - a.date.toMillis())
                  .map((evenement) => (
                    <div key={evenement.id} className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold text-gray-600">
                        {getEvenementIcon(evenement.type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div className="font-medium text-gray-900">{evenement.titre}</div>
                          <div className="text-sm text-gray-500">{formatDate(evenement.date)}</div>
                        </div>
                        {evenement.description && (
                          <p className="text-gray-600 text-sm mt-1">{evenement.description}</p>
                        )}
                        {evenement.auteurNom && (
                          <p className="text-gray-400 text-xs mt-1">Par {evenement.auteurNom}</p>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Colonne droite - Éléments liés */}
          <div className="space-y-6">
            {/* Interlocuteurs */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Interlocuteurs</h2>
              {interlocuteursLies.length === 0 ? (
                <p className="text-gray-500 text-sm">Aucun interlocuteur lié</p>
              ) : (
                <ul className="space-y-2">
                  {interlocuteursLies.map((interlocuteur) => (
                    <li key={interlocuteur.id} className="text-sm">
                      <Link 
                        to={`/admin/interlocuteurs/${interlocuteur.id}`}
                        className="text-blue-600 hover:underline"
                      >
                        {interlocuteur.raisonSociale}
                      </Link>
                      <span className="text-gray-500 ml-2">({interlocuteur.type})</span>
                      {interlocuteur.actif === false && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
                          Suspendu
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Signalements */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Signalements ({signalementsLies.length})</h2>
                <button
                  onClick={() => setShowRattachementSignalement(true)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  + Rattacher
                </button>
              </div>
              {signalementsLies.length === 0 ? (
                <p className="text-gray-500 text-sm">Aucun signalement rattaché</p>
              ) : (
                <ul className="space-y-3">
                  {signalementsLies.map((signalement) => (
                    <li key={signalement.id} className="flex justify-between items-start border-b pb-2">
                      <div>
                        <Link 
                          to={`/admin/signalements/${signalement.id}`}
                          className="text-blue-600 hover:underline text-sm font-medium"
                        >
                          {signalement.titre}
                        </Link>
                        <div className="text-xs text-gray-500">
                          {formatDateShort(signalement.createdAt)}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDetacherSignalement(signalement.id)}
                        className="text-red-500 hover:text-red-700 text-xs"
                      >
                        Détacher
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Documents */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Documents ({documentsLies.length})</h2>
                <button
                  onClick={() => setShowRattachementDocument(true)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  + Rattacher
                </button>
              </div>
              {documentsLies.length === 0 ? (
                <p className="text-gray-500 text-sm">Aucun document rattaché</p>
              ) : (
                <ul className="space-y-3">
                  {documentsLies.map((doc) => (
                    <li key={doc.id} className="flex justify-between items-start border-b pb-2">
                      <div>
                        <Link 
                          to={`/admin/documents/${doc.id}`}
                          className="text-blue-600 hover:underline text-sm font-medium"
                        >
                          {doc.titre}
                        </Link>
                        <div className="text-xs text-gray-500">
                          {formatDateShort(doc.dateDocument)}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDetacherDocument(doc.id)}
                        className="text-red-500 hover:text-red-700 text-xs"
                      >
                        Détacher
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Actions</h2>
              {/* Session 75 : Bouton Supprimer visible uniquement pour Gestionnaire/Admin SaaS */}
              {canDeleteAffaire && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Supprimer l'affaire
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Modal Changement Statut */}
        {showChangementStatut && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Changer le statut</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nouveau statut
                  </label>
                  <select
                    value={nouveauStatut}
                    onChange={(e) => setNouveauStatut(e.target.value as StatutAffaire)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    {Object.entries(STATUT_AFFAIRE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Commentaire (optionnel)
                  </label>
                  <textarea
                    value={commentaireStatut}
                    onChange={(e) => setCommentaireStatut(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Raison du changement..."
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setShowChangementStatut(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-900"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleChangementStatut}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Valider
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Ajout Note */}
        {showAjoutNote && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Ajouter une note</h3>
              <div className="space-y-4">
                <textarea
                  value={noteTexte}
                  onChange={(e) => setNoteTexte(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Votre note..."
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => { setShowAjoutNote(false); setNoteTexte(''); }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-900"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleAjoutNote}
                    disabled={!noteTexte.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    Ajouter
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Rattachement Signalement */}
        {showRattachementSignalement && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4">Rattacher un signalement</h3>
              {signalementsDisponibles.length === 0 ? (
                <p className="text-gray-500">Aucun signalement disponible à rattacher.</p>
              ) : (
                <ul className="space-y-2">
                  {signalementsDisponibles.map((sig) => (
                    <li 
                      key={sig.id}
                      className="flex justify-between items-center p-3 border rounded-lg hover:bg-gray-50"
                    >
                      <div>
                        <div className="font-medium">{sig.titre}</div>
                        <div className="text-sm text-gray-500">{formatDateShort(sig.createdAt)}</div>
                      </div>
                      <button
                        onClick={() => handleRattacherSignalement(sig.id)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Rattacher
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => setShowRattachementSignalement(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Rattachement Document */}
        {showRattachementDocument && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4">Rattacher un document</h3>
              {documentsDisponibles.length === 0 ? (
                <p className="text-gray-500">Aucun document disponible à rattacher.</p>
              ) : (
                <ul className="space-y-2">
                  {documentsDisponibles.map((doc) => (
                    <li 
                      key={doc.id}
                      className="flex justify-between items-center p-3 border rounded-lg hover:bg-gray-50"
                    >
                      <div>
                        <div className="font-medium">{doc.titre}</div>
                        <div className="text-sm text-gray-500">{formatDateShort(doc.dateDocument)}</div>
                      </div>
                      <button
                        onClick={() => handleRattacherDocument(doc.id, doc.titre)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Rattacher
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => setShowRattachementDocument(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Suppression - Double confirmation */}
        {showDeleteModal && affaire && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                {/* Icône alerte */}
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-red-100">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>

                {/* Titre */}
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  Confirmer la suppression
                </h3>

                {/* Message */}
                <p className="text-gray-600 text-center mb-2">
                  Voulez-vous vraiment supprimer cette affaire ?
                </p>
                <p className="text-sm font-medium text-gray-900 text-center mb-4 px-4 py-2 bg-gray-100 rounded">
                  {affaire.titre}
                </p>
                <p className="text-sm text-gray-500 text-center mb-2">
                  Les signalements et documents rattachés ne seront pas supprimés.
                </p>
                <p className="text-sm text-red-600 text-center mb-6">
                  Cette action est irréversible.
                </p>

                {/* Boutons */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowDeleteModal(false)}
                    disabled={deleting}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleSupprimer}
                    disabled={deleting}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {deleting ? 'Suppression...' : 'Confirmer la suppression'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}